<template>
    <div class="report-content" data-app>
        <div v-if="isLoading" class="d-flex justify-content-center align-items-center py-4 h-100">
            <v-progress-circular color="primary" indeterminate></v-progress-circular>
        </div>

        <div v-else-if="!selectedColumns.length" class="w-100 h-100 d-flex justify-content-center align-items-center">
            <i class="ma-0">No columns selected or available for preview.</i>
        </div>

        <div class="" v-else>
            <!-- Header Row: Total data on left, pagination + Run Report on right -->
            <div class="column-header-row d-flex align-items-center justify-space-between px-2 py-2">
                <div class="d-flex align-items-center">
                    <h5 class="ma-0 mr-4">Total <b>{{ formattedDataCount }}</b> data based on the applied filters.</h5>
                </div>
            </div>

            <div
                style="height: 1px; background-color: #D7D7D7; width: calc(100% + 32px); margin-left: -16px; margin-right: -16px;">
            </div>

            <!-- Column Preview Title Row with Pagination and Run Report -->
            <div class="column-title-row d-flex align-items-center justify-content-between mx-2 my-4">
                <h4 class="ma-0 m-0 p-0" style="font-size: 16px;"><b>Column Preview</b></h4>

                <div class="d-flex align-items-center" style="gap: 16px;">
                    <!-- Pagination Controls -->
                    <ColumnPagination :template-id="templateId" />

                    <!-- Run Report Button -->
                    <button class="btn btn-outline-primary d-flex align-items-center" @click="runReport">
                        <v-icon small color="#007BFF" class="mr-1">mdi-play-circle</v-icon>
                        Run Report
                    </button>

                </div>
            </div>

            <div class="nav-container">
                <button class="btn-vertical prev" :class="{ 'btn-vertical-primary': fieldPagination.current_page > 1 }"
                    @click="goToPage(fieldPagination.current_page - 1)">
                    <v-icon :color="fieldPagination.current_page > 1 ? '#FFF' : '#D7D7D7'">mdi-chevron-left</v-icon>
                </button>
                <button class="btn-vertical next"
                    :class="{ 'btn-vertical-primary': fieldPagination.current_page < fieldPagination.total_pages }"
                    @click="goToPage(fieldPagination.current_page + 1)">
                    <v-icon
                        :color="fieldPagination.current_page < fieldPagination.total_pages ? '#FFF' : '#D7D7D7'">mdi-chevron-right</v-icon>
                </button>
            </div>

            <div class="column-container" :style="{ marginTop: isShowLegend ? '108px' : '' }">
                <div class="position-relative columns-wrapper">
                    <div class="d-flex columns-row" :style="columnsRowStyle">
                        <div v-for="(column, index) in processedColumns" :key="index" class="column-item">
                            <div class="column-card">
                                <div class="column-header" :class="{ 'data-dummy': column.isDummy }">
                                    <v-tooltip v-if="!column.isDummy" bottom content-class="tooltip">
                                        <template v-slot:activator="{ on, attrs }">
                                            <h4 v-on="on" class="ma-0"
                                                style="font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                                <b>{{ column.header_name || column.original_header_name }}</b>
                                            </h4>
                                        </template>
                                        <span style="background-color: white;">
                                            <p class="ma-0">{{ column.header_name || column.original_header_name }}</p>
                                            <i style="color: #848484; font-size: 12px;">
                                                <b>Original Column Name :</b>
                                                {{ column.original_header_name }}
                                            </i>
                                        </span>
                                    </v-tooltip>
                                    <div v-else class="d-flex">
                                        <v-icon small color="#3F3F3F" class="mr-2"
                                            style="padding: 2.5px 0;">mdi-file-document-edit-outline</v-icon>
                                        <h4 class="ma-0"
                                            style="font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                            <b>{{ column.header_name || column.original_header_name }}</b>
                                        </h4>
                                    </div>
                                </div>
                                <div class="content-container"
                                    :style="{ backgroundColor: column.isDummy ? '#F4F4F4' : '#FFFFFF' }">
                                    <div v-if="column.isQuestionnaire">
                                        <template v-if="column.previewData.length">
                                            <div class="content-preview" v-for="(item, index) in column.previewData"
                                                :key="index">
                                                <h4 class="ma-0" style="font-size: 16px;">
                                                    <span v-if="item || item === 0">{{ item }}</span>
                                                    <i v-else class="text-muted"
                                                        style="color: #DDDDDD !important;">Empty</i>
                                                </h4>
                                            </div>
                                        </template>
                                        <div v-else>
                                            <i class="text-muted text-center d-block"
                                                style="color: #BDBDBD !important;">Preview unavailable.
                                                Ensure
                                                your organization has data to display.</i>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-center align-items-start position-relative"
                                        style="height: 802px; color: #007BFF;"
                                        v-else-if="!showPreview[`${column.category_key || column.group}_${column.columnName}`]">
                                        <button @click="togglePreview(column)" class="d-flex align-items-center"
                                            style="position: sticky; top: 60%; transform: translateY(-50%);">
                                            <v-icon small color="#007BFF" class="mr-2"
                                                style="height: fit-content;">mdi-eye-outline</v-icon>
                                            Show Preview Data
                                        </button>
                                    </div>
                                    <div
                                        v-if="showPreview[`${column.category_key || column.group}_${column.columnName}`]">
                                        <template
                                            v-if="previewData[`${column.category_key || column.group}_${column.columnName}`] && previewData[`${column.category_key || column.group}_${column.columnName}`].length > 0">
                                            <div class="content-preview"
                                                v-for="(item, index) in previewData[`${column.category_key || column.group}_${column.columnName}`]"
                                                :key="index">
                                                <h4 class="ma-0" style="font-size: 16px;">
                                                    <span v-if="item || item === 0">
                                                        <span v-if="column.columnName === 'prevailing_wage_cents'">{{
                                                            formatCurrency(item) }}</span>
                                                        <span v-else>{{ item }}</span>
                                                    </span>
                                                    <i v-else class="text-muted"
                                                        style="color: #DDDDDD !important;">Empty</i>
                                                </h4>
                                            </div>
                                        </template>
                                        <div v-else>
                                            <i class="text-muted text-center d-block"
                                                style="color: #BDBDBD !important;">Preview unavailable.
                                                Ensure
                                                your organization has data to display.</i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <RenameColumnModal v-if="showModal" :dialog="showModal" @toggle-modal="toggleModal" :dataColumn="modalData" />
    </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters, mapState } from 'vuex';
import RenameColumnModal from '../report_templates/templates_detail/RenameColumnModal.vue';
import ColumnPagination from './ColumnPagination.vue';
import { resolveDynamicFiltersToAbsolute } from '../../shared/report-filter-helpers';

export default {
    name: 'ScheduledReportColumnDetail',
    components: {
        RenameColumnModal,
        ColumnPagination,
    },
    props: {
        templateId: {
            type: [String, Number],
            required: true
        },
        scheduleId: {
            type: [String, Number],
            default: null
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        ...mapGetters({
            selectedColumns: 'reportTemplates/selectedColumns',
            originalColumns: 'reportTemplates/originalColumns',
            hasUnsavedChanges: "reportTemplates/hasUnsavedChanges",
        }),
        ...mapState('reportTemplates', ['allReportFields', 'fieldPagination', 'isLoading', 'reportTemplate']),
        ...mapState('reportList', ['previewDataIds', 'dataCount']),
        paginatedColumns() {
            const startIndex = (this.fieldPagination.current_page - 1) * this.fieldPagination.per_page;
            const endIndex = this.fieldPagination.current_page * this.fieldPagination.per_page;
            return this.selectedColumns.slice(startIndex, endIndex);
        },
        processedColumns() {
            return this.sortedColumns.flatMap(column => {
                if (column.data && Array.isArray(column.data)) {
                    return column.data.map(item => ({
                        ...item,
                        parentGroup: column.group,
                        position: column.position,
                        isDummy: true,
                        isQuestionnaire: column.isQuestionnaire || false,
                        previewData: item.previewData || []
                    }));
                }
                return {
                    ...column,
                    isDummy: false,
                    isQuestionnaire: column.isQuestionnaire || false,
                };
            }).sort((a, b) => a.position - b.position);
        },
        isShowLegend() {
            return this.reportTemplate.type === 'Report::Ticket';
        },
        formattedDataCount() {
            if (this.dataCount == null) return '-';
            return this.dataCount.toLocaleString('en-US');
        },
        columnsPerPage() {
            const perPage = Number(this.fieldPagination?.per_page);
            return Number.isFinite(perPage) && perPage > 0 ? perPage : 5;
        },
        columnsRowStyle() {
            return {
                '--preview-columns-per-page': this.columnsPerPage,
            };
        },
    },
    data() {
        return {
            showModal: false,
            modalData: null,
            showPreview: {},
            previewData: {},
            sortedColumns: [],
            isRunningReport: false,
            filterDebounceTimer: null,
        }
    },
    methods: {
        ...mapActions({
            getReportFields: 'reportTemplates/getReportFields',
            runReportSchedule: 'reportSchedules/runReportSchedule',
        }),
        ...mapMutations({
            setPagination: 'reportTemplates/setFieldPagination',
            setAppliedFilters: 'reportTemplates/setAppliedFilters',
        }),
        async runReport() {
            if (this.scheduleId) {
                this.isRunningReport = true;
                try {
                    await this.runReportSchedule({ id: this.scheduleId, params: (this.filters || {}) });
                    this.$emit('run-report');
                } catch (error) {
                    console.error('Error running scheduled report:', error);
                } finally {
                    this.isRunningReport = false;
                }
                return;
            }

            if (!this.templateId) return;

            this.isRunningReport = true;
            try {
                this.setAppliedFilters(this.filters || {});
                await this.$store.dispatch('reportTemplates/exportReport', this.templateId);
                this.$emit('run-report');
            } catch (error) {
                console.error('Error running report:', error);
            } finally {
                this.isRunningReport = false;
            }
        },
        async refreshPreviewData() {
            if (!this.templateId) return;

            // Reset preview data
            this.showPreview = {};
            this.previewData = {};

            // Resolve dynamic date filters to absolute dates for preview
            const resolvedFilters = resolveDynamicFiltersToAbsolute(this.filters || {});

            const params = {
                id: this.templateId,
                params: resolvedFilters
            };

            // Dispatch to reportList module which supports filter params
            await this.$store.dispatch('reportList/getPreviewDataIds', params);

            // Fetch updated data count
            await this.$store.dispatch('reportList/getDataCount', params);
        },
        openModal(column) {
            this.showModal = true;
            this.modalData = { ...column };
        },
        toggleModal(payload) {
            this.showModal = payload;
        },
        removeColumn(column) {
            const originalIndex = this.originalColumns.findIndex(orig => orig.columnName === column.columnName && orig.group === column.group);

            if (originalIndex !== -1) {
                this.originalColumns[originalIndex]._destroy = 1;
            }

            const updatedColumns = this.selectedColumns.filter(
                col => !(col.group === column.group && col.columnName === column.columnName)
            );

            const perPage = this.fieldPagination.per_page;
            const totalCount = updatedColumns.length;
            const totalPages = Math.ceil(totalCount / perPage);

            let currentPage = this.fieldPagination.current_page;

            if (currentPage > totalPages) {
                currentPage = totalPages;
            }

            this.$store.commit('reportTemplates/setSelectedColumns', updatedColumns);
            this.$store.commit('reportTemplates/setUnsavedChanges', true);
            this.$store.commit('reportTemplates/setFieldPagination', {
                current_page: currentPage,
                per_page: perPage,
                total_count: totalCount,
                total_pages: totalPages,
                count: perPage,
            });
        },
        goToPage(page) {
            if (page < 1 || page > this.fieldPagination.total_pages) return;

            if (this.hasUnsavedChanges) {
                this.setPagination({
                    current_page: page,
                    per_page: this.fieldPagination.per_page,
                    total_count: this.selectedColumns.length,
                    total_pages: Math.ceil(this.selectedColumns.length / this.fieldPagination.per_page),
                });
            } else {
                this.setPagination({
                    current_page: page,
                    per_page: this.fieldPagination.per_page,
                });

                if (this.templateId) {
                    this.getReportFields(this.templateId);
                }
            }
        },
        async togglePreview(column) {
            const uniqueKey = `${column.category_key || column.group}_${column.column_name || column.columnName}`;
            const templateId = this.templateId;
            const fieldId = column.id

            try {
                const previewData = await this.$store.dispatch('reportTemplates/getColumnPreview', {
                    templateId,
                    fieldId,
                    column,
                    previewDataIds: this.previewDataIds
                });

                if (previewData) {
                    this.$set(this.previewData, uniqueKey, previewData);
                    this.$set(this.showPreview, uniqueKey, true);
                }
            } catch (error) {
                console.error("Error in togglePreview:", error);
            }
        },
        async fetchPreviewData(column) {
            try {
                const previewData = await this.$store.dispatch('reportTemplates/getColumnPreview', {
                    templateId: this.templateId,
                    fieldId: null,
                    column: {
                        columnName: column.columnName,
                        group: 'Questionnaire::Template',
                    },
                    previewDataIds: this.previewDataIds || [],
                });

                return previewData.map(item => ({
                    groupId: `questionnaire_${column.columnName}`,
                    columnName: item.header.toLowerCase().replace(/\s+/g, '_'),
                    header_name: item.header,
                    original_header_name: item.header,
                    previewData: item.data || [],
                }));
            } catch (error) {
                console.error("Failed to fetch questionnaire template preview:", error);
                return [];
            }
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(value / 100);
        },
    },
    watch: {
        templateId: {
            async handler(newId, oldId) {
                if (newId && newId !== oldId) {
                    // Reset preview data
                    this.showPreview = {};
                    this.previewData = {};

                    // Re-fetch columns for the new template
                    const fields = await this.$store.dispatch('reportTemplates/getReportFields', newId);

                    // Process and set selectedColumns locally (without modifying the store action)
                    if (fields && fields.length > 0) {
                        this.$store.commit('reportTemplates/processSelectedColumns', fields);
                    }

                    // Optionally, refresh preview data
                    this.refreshPreviewData();
                }
            }
        },
        paginatedColumns: {
            immediate: true,
            deep: true,
            async handler(newColumns) {
                this.sortedColumns = await Promise.all(
                    newColumns.map(async (column) => {
                        const localColumn = { ...column };
                        if (localColumn.group === 'Questionnaire::Template' && localColumn.id) {
                            if (!localColumn.data || localColumn.data.length === 0) {
                                localColumn.data = await this.fetchPreviewData(localColumn);
                            }
                        }
                        return localColumn;
                    })
                );
            }
        },
        previewDataIds: {
            handler(newVal) {
                if (newVal) {
                    this.showPreview = {};
                    this.previewData = {};
                }
            },
        },
        filters: {
            deep: true,
            handler(newFilters, oldFilters) {
                // Skip if templateId is not set
                if (!this.templateId) return;

                // Debounce the API call to avoid too many requests
                if (this.filterDebounceTimer) {
                    clearTimeout(this.filterDebounceTimer);
                }

                this.filterDebounceTimer = setTimeout(() => {
                    this.refreshPreviewData();
                }, 500); // 500ms debounce
            }
        }
    },
    async created() {
        if (this.templateId) {
            // Fetch all report fields (Structure)
            await this.$store.dispatch('reportTemplates/getAllReportFields', this.templateId);
            // Process selected columns from all fields
            this.$store.commit('reportTemplates/processSelectedColumns', this.$store.state.reportTemplates.allReportFields);

            // Set field pagination based on processed columns
            const allFields = this.$store.state.reportTemplates.allReportFields;
            const perPage = this.$store.state.reportTemplates.fieldPagination?.per_page || 5;
            this.$store.commit('reportTemplates/setFieldPagination', {
                current_page: 1,
                per_page: perPage,
                total_count: allFields.length,
                total_pages: Math.ceil(allFields.length / perPage),
                count: Math.min(allFields.length, perPage)
            });

            // Fetch preview data and count (Data) using the new logic
            this.refreshPreviewData();
        }
    }
}
</script>

<style lang="scss" scoped>
.report-content {
    width: 100%;
    overflow: visible;
    color: #3F3F3F;
    position: relative;
    padding-right: 8px;


    .index {
        width: fit-content;
        background: white;
        border: 1px solid #BDBDBD;
        border-radius: 4px;
        padding: 8px;
    }

    .filter {
        width: 120px;
        background: white;
        border: 1px solid #BDBDBD;
        border-radius: 4px;
        padding: 8px;
    }

    .column-container {
        overflow: auto;
        height: calc(100vh - 500px);
        padding: 0 20px;
        position: relative;
    }

    .nav-container {
        display: flex;
        justify-content: space-between;
        width: 100%;
        height: fit-content !important;
        position: absolute;
        top: 70%;
        pointer-events: none;
        z-index: 1;
    }

    .btn-vertical {
        position: sticky;
        top: 50%;
        transform: translateY(-50%);
        z-index: 2;
        background: white;
        border: 1px solid #D7D7D7;
        border-radius: 12px 14px;
        padding: 6px 8px 8px 8px;
        cursor: pointer;
        box-shadow: 0px 3px 4px 0px #00000026;
        pointer-events: initial;
    }

    .btn-vertical.prev {
        left: 2px;
    }

    .btn-vertical.next {
        right: 14px;
    }

    .btn-vertical-primary {
        background-color: #007BFF;
        color: white !important;
    }

    .columns-wrapper {
        width: 100%;
    }

    .columns-row {
        width: 100%;
        gap: var(--preview-column-gap, 8px);
    }

    .column-item {
        flex: 0 0 calc((100% - (var(--preview-column-gap, 8px) * (var(--preview-columns-per-page, 5) - 1))) / var(--preview-columns-per-page, 5));
        min-width: 200px;
        max-width: calc((100% - (var(--preview-column-gap, 8px) * (var(--preview-columns-per-page, 5) - 1))) / var(--preview-columns-per-page, 5));
    }

    .column-card {
        width: 100%;
        border: 1px solid #D7D7D7;
        border-radius: 8px;
    }

    .column-header {
        background-color: #EDF4FE;
        padding: 10px 16px;
        display: flex;
        gap: 6px;
        align-items: center;
        justify-content: space-between;
        border-radius: 8px 8px 0 0;
    }

    .data-dummy {
        background-color: #D7D7D7 !important;
    }

    .content-container {
        background-color: #FFFFFF;
        height: auto;
    }

    .content-preview {
        padding: 10px 16px;
        border-bottom: 1px solid #D7D7D7;
    }

    .content-preview h4 {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
}

.column-pagination {
    color: #3F3F3F;
    font-size: 14px;
    position: relative;
    z-index: 1;
}

.custom-tooltip {
    opacity: 1 !important;
}

::v-deep .v-tooltip {
    z-index: 99 !important;
}
</style>
